import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import t from "../../../../../../../i18n";
import DateRangePicker from "@ui/DateRangePicker/DateRangePicker";
import type { DateValue } from "@internationalized/date";
import { getLocalTimeZone, parseAbsolute, today } from "@internationalized/date";
import { locale } from "../../../../../../../i18n/locale";
import type { DurationT, PeriodT } from "../../../../schema/period";
import { spacings } from "@ui/Theme";
import { css } from "@linaria/core";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { useDashboardEditorContext } from "../../../../hook/context/editor";
import type { BlockPlotModelT } from "../../../../schema/block/plot";
import type { BlockDiagramModelT } from "../../../../schema/block/diagram";
import type { RangeValue } from "@react-types/shared";
import { useProject } from "../../../../../../../api/useProjects";
import { useProjectPeriod } from "../../../../../useProjectPeriod";
import { useProjectSource } from "../../../../../../../api/useSources";
import useActiveAnalysesStream from "../../../../../../../api/useActiveAnalysesStream";
import useGroups from "../../../../../../../api/useGroups";
import { baseAggregationPeriodPresets } from "../../../../../../../domain/aggregation/preset";

const TIME_ZONE = getLocalTimeZone();

const datasetRangeMessage = css`
  margin-top: ${spacings.xs};
`;

export const BlockDateRangeSelector = memo(
  ({
    projectId,
    block,
    defaultPreset = "ALL",
  }: {
    projectId: string;
    block: BlockPlotModelT | BlockDiagramModelT;
    defaultPreset?: DurationT;
  }) => {
    const editor = useDashboardEditorContext();
    const [dateTimeError, setDateTimeError] = useState<string>();
    const [dateTimeInfo, setDateTimeInfo] = useState<string>();

    const { activeAnalyses } = useActiveAnalysesStream(projectId);
    const project = useProject(projectId);
    if (!project) {
      throw new Error("No project");
    }

    const source = useProjectSource(project);
    if (!source) {
      throw new Error("No source");
    }
    const [, , extremes] = useProjectPeriod({
      project,
      source,
      analyses: activeAnalyses,
      boundaries: "active-analyses",
    });

    const dateBoundaries = useMemo(
      () => ({
        startDate: parseAbsolute(new Date(extremes.from).toISOString(), project.timezone),
        endDate: parseAbsolute(new Date(extremes.to).toISOString(), project.timezone),
      }),
      [extremes, project.timezone],
    );

    const groups = useGroups(projectId);
    const presets = useMemo(
      () =>
        baseAggregationPeriodPresets({
          limit: { from: new Date(extremes.from), to: new Date() },
          timezone: TIME_ZONE,
          groups,
        }),
      [extremes.from, groups],
    );

    const resolvePresetRange = useCallback(
      (presetId: DurationT) => {
        const preset = presets.find(p => p.metadata?.["id"] === presetId);
        if (!preset)
          return {
            start: dateBoundaries?.startDate ?? today(TIME_ZONE),
            end: dateBoundaries?.endDate ?? today(TIME_ZONE),
          };
        return {
          start: typeof preset.startDate === "function" ? preset.startDate() : preset.startDate,
          end: typeof preset.endDate === "function" ? preset.endDate() : preset.endDate,
        };
      },
      [presets, dateBoundaries],
    );

    const resolvePeriodRange = useCallback(
      (period: BlockPlotModelT["data"]["period"]) => {
        if (typeof period === "string") {
          return resolvePresetRange(period as DurationT);
        }
        if (typeof period === "object" && period !== null) {
          let start, end;
          if (typeof period.from === "string" && typeof period.to === "string") {
            start = parseAbsolute(period.from, TIME_ZONE);
            end = parseAbsolute(period.to, TIME_ZONE);
          }
          if (typeof period.from === "number" && typeof period.to === "number") {
            start = parseAbsolute(new Date(period.from).toISOString(), TIME_ZONE);
            end = parseAbsolute(new Date(period.to).toISOString(), TIME_ZONE);
          }
          if (start && end) {
            return { start, end };
          }
        }
        return {
          start: dateBoundaries?.startDate ?? today(TIME_ZONE),
          end: dateBoundaries?.endDate ?? today(TIME_ZONE),
        };
      },
      [resolvePresetRange, dateBoundaries],
    );

    const [dateRangeValue, setDateRangeValue] = useState(() => {
      const period = block?.data?.period;
      const resolved = resolvePeriodRange(period);
      if (resolved) return resolved;

      return resolvePresetRange(defaultPreset);
    });

    useEffect(() => {
      const period = block?.data?.period;
      const resolved = resolvePeriodRange(period);
      if (resolved) {
        setDateRangeValue(resolved);
        return;
      }

      const defaultDateRange = resolvePresetRange(defaultPreset);
      setDateRangeValue(defaultDateRange);
    }, [
      block?.data?.period,
      dateBoundaries,
      defaultPreset,
      presets,
      resolvePresetRange,
      resolvePeriodRange,
    ]);

    const updateBlock = useCallback(
      (value: BlockPlotModelT["data"]["period"]) => {
        if (!block) return;
        void editor?.updateBlock({
          ...block,
          data: {
            ...block.data,
            period: value,
          },
        });
      },
      [block, editor],
    );

    const onDateValueChange = useCallback(
      (value: RangeValue<DateValue> | null) => {
        if (!value) {
          return;
        }
        setDateTimeError(undefined);
        setDateTimeInfo(undefined);

        /** @note Non-blocking message issue */
        if (dateBoundaries) {
          if (value.start.compare(dateBoundaries.startDate) < 0) {
            const date = dateBoundaries.startDate.toDate().toLocaleDateString(locale);
            setDateTimeInfo(t("daterangepicker.error.start_lt_limit", date));
          }

          if (value.start.compare(value.end) > 0) {
            setDateTimeError(t("daterangepicker.error.start_gt_end"));
          }

          if (value.end.compare(value.start) < 0) {
            setDateTimeError(t("daterangepicker.error.end_lt_start"));
          }
        }

        const from = value.start.toString();
        const to = value.end.toString();
        const preset = presets.find(p => {
          const s = typeof p.startDate === "function" ? p.startDate() : p.startDate;
          const e = typeof p.endDate === "function" ? p.endDate() : p.endDate;
          return s.toString() === from && e.toString() === to;
        });

        const period =
          preset && preset.metadata && typeof preset.metadata["id"] === "string"
            ? (preset.metadata["id"] as PeriodT)
            : {
                from: value.start.toDate(TIME_ZONE).toISOString(),
                to: value.end.toDate(TIME_ZONE).toISOString(),
              };

        setDateRangeValue(value);
        updateBlock(period);
      },
      [updateBlock, dateBoundaries, presets],
    );

    if (!block) return null;

    return (
      <>
        <DateRangePicker
          id=""
          presets={presets}
          popoverPlacement="bottom end"
          label={
            block.kind === "DIAGRAM"
              ? t("project.dashboard.editor.gui.section.aggregation_range.title")
              : t("project.dashboard.editor.gui.section.time_range.title")
          }
          dateOptions={{
            shouldCloseOnSelect: true,
            hideTimeZone: true,
            granularity: "day",
            minValue: dateBoundaries?.startDate,
            maxValue: dateBoundaries?.endDate,
            value: dateRangeValue,
            isDateUnavailable: date => {
              if (dateBoundaries?.startDate && date.compare(dateBoundaries?.startDate) < 0)
                return true;
              if (dateBoundaries?.endDate && date.compare(dateBoundaries?.endDate) > 0) return true;
              return false;
            },
            onChange: onDateValueChange,
          }}
        />
        {dateTimeError && (
          <AdditionnalMessage className={datasetRangeMessage} status="ERROR" text={dateTimeError} />
        )}
        {dateTimeInfo && !dateTimeError && (
          <AdditionnalMessage className={datasetRangeMessage} status="INFO" text={dateTimeInfo} />
        )}
      </>
    );
  },
);
